import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { leaderboardStore } from '../stores/leaderboardStore'
import { authStore } from '../stores/authStore'
import { profileStore } from '../stores/profileStore'
import { gamificationStore } from '../stores/gamificationStore'
import { playSound } from '../utils/sounds'

const GAMES = [
  { id: 'all', name: 'All', emoji: '🏆', gradient: 'from-amber-500 to-orange-500' },
  { id: 'hitmoji', name: 'HitMoji', emoji: '💥', gradient: 'from-orange-500 to-red-500' },
  { id: 'findmoji', name: 'FindMoji', emoji: '🔍', gradient: 'from-emerald-500 to-green-500' },
  { id: 'memoji', name: 'Memoji', emoji: '🧠', gradient: 'from-violet-500 to-purple-500' },
  { id: 'evoji', name: 'Evoji', emoji: '🧬', gradient: 'from-pink-500 to-rose-500' },
  { id: 'quizmoji', name: 'QuizMoji', emoji: '📚', gradient: 'from-purple-500 to-pink-500' },
  { id: 'chessmoji', name: 'ChessMoji', emoji: '♟️', gradient: 'from-purple-600 to-indigo-600' },
]

const Leaderboard = observer(() => {
  const { t } = useTranslation()
  const [selectedGame, setSelectedGame] = useState<string>('all')
  const [animateIn, setAnimateIn] = useState(false)

  useEffect(() => {
    leaderboardStore.fetchLeaderboard(selectedGame === 'all' ? undefined : selectedGame)
    setAnimateIn(false)
    setTimeout(() => setAnimateIn(true), 100)
  }, [selectedGame])

  const handleGameSelect = (gameId: string) => {
    playSound('click')
    setSelectedGame(gameId)
  }

  const entries = leaderboardStore.entries
  const currentGame = GAMES.find(g => g.id === selectedGame)

  return (
    <div className={`min-h-screen bg-gradient-to-br ${currentGame?.gradient || 'from-amber-500 to-orange-500'} transition-all duration-500`}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/20 backdrop-blur-md border-b border-white/10">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link 
                to="/"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <span>←</span>
              </Link>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{currentGame?.emoji}</span>
                <div>
                  <h1 className="font-title text-lg text-white">{t('leaderboard.title')}</h1>
                  <p className="text-white/60 text-xs">{t('leaderboard.players', { count: entries.length })}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4">
        {/* Game Filter Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
          {GAMES.map(game => (
            <button
              key={game.id}
              onClick={() => handleGameSelect(game.id)}
              className={`
                flex items-center gap-2 px-4 py-2.5 rounded-2xl whitespace-nowrap
                transition-all duration-300 font-medium
                ${selectedGame === game.id 
                  ? 'bg-white text-gray-800 shadow-lg scale-105' 
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
                }
              `}
            >
              <span className="text-lg">{game.emoji}</span>
              <span>{game.name}</span>
            </button>
          ))}
        </div>

        {/* Your Profile Card - Always visible */}
        <div className="bg-gradient-to-r from-purple-500/30 to-pink-500/30 backdrop-blur-sm rounded-2xl p-4 mb-4 border border-purple-400/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center text-3xl shadow-lg border-2 border-white/30">
                  {profileStore.emoji}
                </div>
                <span className="absolute -bottom-1 -right-1 text-lg">{profileStore.flag}</span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-white font-bold text-lg">{profileStore.username}</p>
                  <span className="bg-purple-500/50 text-white text-[10px] px-2 py-0.5 rounded-full">{t('common.you')}</span>
                </div>
                <p className="text-white/70 text-sm">{t('common.level')} {gamificationStore.level} • {profileStore.scoreForm} {t('common.pts')}</p>
              </div>
            </div>
            {leaderboardStore.userRank ? (
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center text-xl font-bold text-white shadow-lg">
                  #{leaderboardStore.userRank}
                </div>
                <p className="text-white/60 text-xs mt-1">{t('common.rank')}</p>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
                  🎮
                </div>
                <p className="text-white/60 text-xs mt-1">{t('leaderboard.playToRank')}</p>
              </div>
            )}
          </div>
          
          {/* Best score in current filter */}
          {leaderboardStore.userBestScore > 0 && (
            <div className="mt-3 pt-3 border-t border-white/10 flex justify-between items-center">
              <span className="text-white/60 text-sm">{t('leaderboard.yourBest', { game: selectedGame !== 'all' ? currentGame?.name : '' })}</span>
              <span className="text-white font-bold">{leaderboardStore.userBestScore.toLocaleString()} pts</span>
            </div>
          )}
        </div>

        {/* Top 3 Podium */}
        {entries.length >= 3 && (
          <div className="flex justify-center items-end gap-2 mb-6 px-4">
            {/* 2nd Place */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-3xl mb-2 border-2 border-gray-300">
                {entries[1]?.emoji || '👤'}
              </div>
              <div className="bg-gradient-to-t from-gray-400 to-gray-300 w-20 h-20 rounded-t-xl flex flex-col items-center justify-end pb-2">
                <span className="text-2xl">🥈</span>
                <p className="text-gray-800 font-bold text-xs truncate max-w-16 px-1">
                  {entries[1]?.username?.slice(0, 8) || 'Player'}
                </p>
                <p className="text-gray-700 text-xs font-bold">{entries[1]?.score}</p>
              </div>
            </div>
            
            {/* 1st Place */}
            <div className="flex flex-col items-center -mt-4">
              <div className="text-3xl animate-bounce mb-1">👑</div>
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center text-4xl mb-2 border-2 border-yellow-300 shadow-lg">
                {entries[0]?.emoji || '👤'}
              </div>
              <div className="bg-gradient-to-t from-yellow-500 to-yellow-400 w-24 h-28 rounded-t-xl flex flex-col items-center justify-end pb-2">
                <span className="text-3xl">🥇</span>
                <p className="text-gray-800 font-bold text-sm truncate max-w-20 px-1">
                  {entries[0]?.username?.slice(0, 10) || 'Player'}
                </p>
                <p className="text-gray-800 font-bold">{entries[0]?.score}</p>
              </div>
            </div>
            
            {/* 3rd Place */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-3xl mb-2 border-2 border-orange-400">
                {entries[2]?.emoji || '👤'}
              </div>
              <div className="bg-gradient-to-t from-orange-500 to-orange-400 w-20 h-16 rounded-t-xl flex flex-col items-center justify-end pb-2">
                <span className="text-2xl">🥉</span>
                <p className="text-gray-800 font-bold text-xs truncate max-w-16 px-1">
                  {entries[2]?.username?.slice(0, 8) || 'Player'}
                </p>
                <p className="text-gray-700 text-xs font-bold">{entries[2]?.score}</p>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard List */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10">
          <div className="p-3 border-b border-white/10">
            <p className="text-white/60 text-sm text-center">{t('leaderboard.allPlayers')}</p>
          </div>
          
          {leaderboardStore.loading ? (
            <div className="p-8 text-center">
              <span className="text-4xl animate-spin inline-block mb-2">🏆</span>
              <p className="text-white/60">{t('leaderboard.loading')}</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="p-8 text-center">
              <span className="text-5xl block mb-3">🎮</span>
              <p className="text-white/90 font-bold text-lg">{t('leaderboard.noScores')}</p>
              <p className="text-white/60 text-sm mb-4">{t('leaderboard.beFirst')}</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-gray-800 rounded-xl font-semibold hover:scale-105 transition-transform"
              >
                <span>🎯</span>
                <span>{t('leaderboard.playNow')}</span>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {entries.slice(3).map((entry, index) => {
                const rank = index + 4
                const isCurrentUser = entry.user_id === authStore.userId
                
                return (
                  <div 
                    key={entry.id || rank}
                    className={`
                      flex items-center gap-3 p-3 transition-all duration-300
                      ${isCurrentUser ? 'bg-white/20' : 'hover:bg-white/5'}
                      ${animateIn ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}
                    `}
                    style={{ transitionDelay: `${index * 30}ms` }}
                  >
                    {/* Rank */}
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white/60 text-sm font-bold">
                      {rank}
                    </div>

                    {/* Avatar */}
                    <div className="relative">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                        isCurrentUser ? 'bg-gradient-to-br from-yellow-400 to-orange-500' : 'bg-white/20'
                      }`}>
                        {entry.emoji || '👤'}
                      </div>
                      <span className="absolute -bottom-1 -right-1 text-sm">{entry.flag}</span>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold truncate ${isCurrentUser ? 'text-yellow-300' : 'text-white'}`}>
                        {entry.username || 'Anonymous'}
                        {isCurrentUser && <span className="text-yellow-400/80 text-xs ml-1">({t('common.you')})</span>}
                      </p>
                      <div className="flex items-center gap-2 text-white/50 text-xs">
                        <span>{GAMES.find(g => g.id === entry.game)?.emoji || '🎮'}</span>
                        <span>{t('common.level')} {entry.level + 1}</span>
                      </div>
                    </div>

                    {/* Score */}
                    <div className="text-right">
                      <p className="text-white font-bold text-lg">{entry.score.toLocaleString()}</p>
                      <p className="text-white/40 text-xs">{t('common.pts')}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Play CTA */}
        <div className="mt-6 text-center pb-6">
          <p className="text-white/60 text-sm mb-3">{t('leaderboard.readyToClimb')}</p>
          <div className="flex justify-center gap-3">
            <Link
              to="/hitmoji"
              className="px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-semibold shadow-lg hover:scale-105 transition-transform"
            >
              💥 HitMoji
            </Link>
            <Link
              to="/findmoji"
              className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl font-semibold shadow-lg hover:scale-105 transition-transform"
            >
              🔍 FindMoji
            </Link>
            <Link
              to="/memoji"
              className="px-4 py-2.5 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl font-semibold shadow-lg hover:scale-105 transition-transform"
            >
              🧠 Memoji
            </Link>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
})

export default Leaderboard
